require('./bootstrap');

import { VueReCaptcha } from 'vue-recaptcha-v3'

import * as Sentry from "@sentry/vue";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "@css/ag-grid.css";
import { AgGridVue } from "ag-grid-vue3";

import { createApp, h } from 'vue';
import { Inertia } from '@inertiajs/inertia'
import { createInertiaApp } from '@inertiajs/inertia-vue3';
import { InertiaProgress } from '@inertiajs/progress';

import { maska } from 'maska';

import { createPinia } from 'pinia';
const pinia = createPinia();

import { useAuthStore } from '@/Stores/auth'

import { usePaymentsStore } from '@/Stores/payments'
const payments = usePaymentsStore(pinia)

import { useErrorsStore } from '@/Stores/errors'

// our global components, registered in the createApp() function below, under mixin()
import { Link, Head } from '@inertiajs/inertia-vue3'
import Button from '@/Shared/VisualComponents/Button.vue';
import Label from '@/Shared/VisualComponents/Label.vue';
import Input from '@/Shared/VisualComponents/Input.vue';
import PromiseButton from "@/Shared/PromiseButton.vue"
import BlockLoader from "@/Shared/VisualComponents/BlockLoader.vue"
import Spinner from "@/images/Spinner.vue"
import SectionCard from '@/Shared/VisualComponents/SectionCard.vue';
import Alert from '@/Shared/VisualComponents/Alert.vue';
import Heading from '@/Shared/VisualComponents/Heading.vue';
import Paragraph from '@/Shared/VisualComponents/Paragraph.vue';
import Quote from '@/Shared/VisualComponents/Quote.vue';

import AppLayout from '@/Layout/AppLayout.vue';

createInertiaApp({
    title: (title) => `${title} - Advance Cairns Members Manager`,
    resolve: (name) => {
        const page = require(`./Network/${name}.vue`).default;
		
		// don't do this on auth pages
		if(name.indexOf('Auth/') < 0) {
            // auto-inject our page layout, allowing pages to override it
        	page.layout = page.layout || h(AppLayout);
		}

        return page;
    },
    setup({ el, app, props, plugin }) {
        const vueApp = createApp({ render: () => h(app, props) })
		
		// add a global errorhandler for vue errors so we can hard-set the replace function if inertia does not call it
		vueApp.config.errorHandler = (err, vm, info) => {
			Inertia.replace(window.location.href)
		}

		Sentry.init({
		  vueApp,
		  dsn: process.env.MIX_SENTRY_DSN,
		  integrations: [
			new Sentry.Replay(),
		  ],
		  environment: process.env.MIX_SENTRY_ENV,
		
		  // Set tracesSampleRate to 1.0 to capture 100%
		  // of transactions for performance monitoring.
		  // We recommend adjusting this value in production
		  tracesSampleRate: 1.0,
		
		  // Capture Replay for 10% of all sessions,
		  // plus for 100% of sessions with an error
		  replaysSessionSampleRate: 0.1,
		  replaysOnErrorSampleRate: 1.0,
		});
            
		vueApp.use(plugin)
            .use(pinia)
			.use(VueReCaptcha, { siteKey: process.env.MIX_RECAPTCHA_SITE_KEY })
            .mixin({ 
				methods: { route },
				components: {
					Link,
					Head,
                    Button,
                    Label,
                    Input,
                    PromiseButton,
                    BlockLoader,
					Spinner,
                    SectionCard,
                    Alert,
                    Heading,
                    Paragraph,
                    Quote,
					AgGridVue,
				}
			})
			.directive('maska', maska)
            .mount(el);

		return vueApp
    },
});

InertiaProgress.init({ color: '#4B5563' });

// handle unsaved changes navigation guard. Return false to cancel navigation
Inertia.on('before', (event) => {
})

// when we get an error from ineria (generally validation errors) we'll show error toasts
Inertia.on('error', (event) => {
	// pull out our errors from errors.detail.errors and create new errors toasts for them
	let errors = useErrorsStore()
	let errorData = Object.entries(event.detail.errors)
	errorData.forEach(([key, value]) => {
		
		// replace underscores with spaces and then caps first letter each word
		key = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
		
		errors.push({
			type: 'error',
            title: key,
            message: value,
		})
	})
})

// add a global interceptor to handle errors
window.axios.interceptors.response.use(
	response => response,
	error => {
		console.log(error);
		// push the error to our errors store
		let errors = useErrorsStore()
		errors.push({
			type: 'error',
			title: error.response.status,
			message: error.response.data.message ?? error.response.statusText,
		})
		return Promise.reject(error)
	}
)

Inertia.on('navigate', (event) => {
	// don't do this on auth pages
	if( event.detail.page.component.indexOf('Auth/') == -1 ) {

		// load up the auth store with the user data
		if( event.detail.page.props.auth ){
			const auth = useAuthStore()
			auth.set(event.detail.page.props.auth)
		}
	}
	// whenever we navigate, close the card dialog
	payments.hideCardForm()
})

// must be run after app.use(pinia) so that useStore() is available
require('@/Stores/resumable')